import {type BillingTermsFormValues} from '../jobs/types'
import {
    createInitialBillingTermsFormValues,
    createInitialFormValues,
    type CreateProjectFormValues,
} from './create-project-form-values'
import type {CreateProjectBillingTermsInput, ProjectBillingTerms} from './schemas'

export type ProjectBillingTermsFormValues = Pick<
    CreateProjectFormValues,
    'billingMethod' | 'billingTerms'
>

/** `StepBillingTerms` is shared with the project wizard, so it wants the full wizard form shape. */
export function toWizardFormValues(values: ProjectBillingTermsFormValues): CreateProjectFormValues {
    return {
        ...createInitialFormValues(),
        billingMethod: values.billingMethod,
        billingTerms: values.billingTerms,
    }
}

export type JobWithLineItems = {
    billableLineItems: Array<{invoiceId: string | null}>
}

export function canEditProjectBillingTerms(jobs: JobWithLineItems[]): boolean {
    return !jobs.some((job) => job.billableLineItems.some((item) => item.invoiceId !== null))
}

export function projectBillingTermsToFormValues(
    terms: ProjectBillingTerms | null,
): ProjectBillingTermsFormValues {
    if (terms == null) {
        return {
            billingMethod: 'line_rates',
            billingTerms: createInitialBillingTermsFormValues(),
        }
    }

    return {
        billingMethod: terms.billingMethod,
        billingTerms: billingTermsToFormValues(terms),
    }
}

function billingTermsToFormValues(terms: ProjectBillingTerms): BillingTermsFormValues {
    return {
        transportationHourlyRate: terms.transportationHourlyRate,
        flatRateAmount: terms.flatRateAmount,
        ratePerLoad: terms.ratePerLoad,
        perLoadNotes: terms.perLoadNotes ?? '',
        demurrageHourlyRate: terms.demurrageHourlyRate,
        disposalFees: terms.disposalFees.map((fee) => ({
            wasteType: fee.wasteType,
            unit: fee.unit,
            unitRate: fee.unitRate,
            estimatedQuantity: fee.estimatedQuantity,
        })),
        washoutFee: terms.washoutFee,
        hseiPercent: terms.hseiPercent,
        managerApprovedBelowMinimumRate: false,
    }
}

export function buildProjectBillingTermsInput(
    values: ProjectBillingTermsFormValues,
    existingTerms: ProjectBillingTerms | null,
): CreateProjectBillingTermsInput {
    const {billingMethod, billingTerms} = values

    return {
        billingMethod,
        transportationHourlyRate:
            billingMethod === 'hourly_transportation'
                ? billingTerms.transportationHourlyRate
                : null,
        estimatedTransportationHours:
            billingMethod === 'hourly_transportation'
                ? (existingTerms?.estimatedTransportationHours ?? null)
                : null,
        flatRateAmount: billingMethod === 'flat_rate' ? billingTerms.flatRateAmount : null,
        ...(billingMethod === 'per_load'
            ? {ratePerLoad: billingTerms.ratePerLoad, perLoadNotes: billingTerms.perLoadNotes}
            : {}),
        demurrageHourlyRate: billingTerms.demurrageHourlyRate,
        disposalFees: billingTerms.disposalFees.map((fee) => ({
            wasteType: fee.wasteType,
            unit: fee.unit,
            unitRate: fee.unitRate ?? 0,
            estimatedQuantity: fee.estimatedQuantity,
        })),
        washoutFee: billingTerms.washoutFee,
        hseiPercent: billingTerms.hseiPercent,
    }
}
